import React from 'react';
import { TooltipHost } from '@fluentui/react';
import styles from './SourceIndicator.module.css';
import { debugLog } from '../../utils/debugLogger';

// Import source icons
import AzureIcon from '../../assets/Azure.svg';
import GoogleIcon from '../../assets/Google.svg';
import BingIcon from '../../assets/Bing.svg';
import BookIcon from '../../assets/Book.svg';
import Office365Icon from '../../assets/Office365.svg';

interface SourceIndicatorProps {
  source?: string;
  confidence?: number;
  document_metadata?: Array<{filename: string; confidence: number; chunks_found?: number}>;
  className?: string;
  isCached?: boolean;  // Whether this response came from FAQ cache
  showCacheBadge?: boolean;  // Whether to show the cache badge (admin toggle)
}

// Map data sources to icons and display names
const sourceIconMap: Record<string, { icon: string; label: string }> = {
  'general_knowledge': { icon: BookIcon, label: 'AI Assistant' },
  'GENERAL_KNOWLEDGE': { icon: BookIcon, label: 'AI Assistant' },
  'internal_knowledge': { icon: BookIcon, label: 'AI Assistant' },  // Legacy
  'INTERNAL_KNOWLEDGE': { icon: BookIcon, label: 'AI Assistant' },  // Legacy
  'real_time_search': { icon: BingIcon, label: 'Bing Search' },
  'live_search': { icon: BingIcon, label: 'Bing Search' },
  'LIVE_SEARCH': { icon: BingIcon, label: 'Bing Search' },
  'bing': { icon: BingIcon, label: 'Bing Search' },
  'bing_search': { icon: BingIcon, label: 'Bing Search' },
  'web_search': { icon: BingIcon, label: 'Web Search' },
  'azure_search': { icon: AzureIcon, label: 'Azure AI Search' },
  'office365': { icon: Office365Icon, label: 'Microsoft 365' },
  'office365_email': { icon: Office365Icon, label: 'Microsoft 365 Email' },
  'office365_calendar': { icon: Office365Icon, label: 'Microsoft 365 Calendar' },
  'office365_files': { icon: Office365Icon, label: 'Microsoft 365 Files' },
  'file_server': { icon: Office365Icon, label: 'File Server' },
  'document_analysis': { icon: BookIcon, label: 'Document Analysis' },
  'analyzing': { icon: BookIcon, label: 'Analyzing' },
  'streaming': { icon: BookIcon, label: 'Processing...' },  // Streaming state
  'deep_research': { icon: BingIcon, label: 'Deep Research' },
  'excel_analysis': { icon: Office365Icon, label: 'Excel Analysis' }
};

// Helper function to get display name for Azure Search indices
const getAzureSearchDisplayName = (indexName: string): string => {
  // Map specific index names to readable labels
  const azureIndexMap: Record<string, string> = {
    'comptroller-reports-index': 'Israeli State Comptroller Reports',
    'bynet-knowledge-base': 'Bynet Knowledge Base',
    'company-policies': 'Company Policies',
    'technical-documentation': 'Technical Documentation',
    // Add more mappings as needed
  };
  
  // Check if we have a specific mapping
  if (azureIndexMap[indexName]) {
    return azureIndexMap[indexName];
  }
  
  // For GUID-like sources, check if they look like UUIDs
  const guidPattern = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
  if (guidPattern.test(indexName)) {
    return 'Azure AI Search';
  }
  
  // Convert kebab-case or underscore names to readable format
  const readable = indexName
    .replace(/[-_]/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
  
  return readable;
};

export const SourceIndicator: React.FC<SourceIndicatorProps> = ({ source, confidence, document_metadata, className, isCached, showCacheBadge = true }) => {
  if (!source) return null;

  let sourceInfo = sourceIconMap[source];
  
  // If source starts with "faq_cache:", extract the original source
  let displaySource = source;
  let isFromCache = isCached || source.startsWith('faq_cache:');
  if (source.startsWith('faq_cache:')) {
    displaySource = source.replace('faq_cache:', '');
    sourceInfo = sourceIconMap[displaySource];
  }
  
  // Debug logging for cache badge
  if (isFromCache) {
    debugLog('[SourceIndicator] ⚡ Cache badge debug:', { source, isCached, isFromCache, showCacheBadge, displaySource });
  }
  
  // If not found in the map, check if it's an Azure Search index
  if (!sourceInfo) {
    // Assume unknown sources are Azure Search indices
    const displayName = getAzureSearchDisplayName(displaySource);
    sourceInfo = { icon: AzureIcon, label: displayName };
  }

  // Cache badge component - only show if showCacheBadge is enabled (admin toggle)
  const CacheBadge = (isFromCache && showCacheBadge) ? (
    <span className={styles.cacheBadge} title="Cached response - type 'search again' for fresh results">
      ⚡
    </span>
  ) : null;

  // For document analysis, show document names and confidence scores
  if (source === 'document_analysis' && document_metadata && document_metadata.length > 0) {
    const documentNames = document_metadata.map(doc => doc.filename).join(', ');
    const avgConfidence = document_metadata.reduce((sum, doc) => sum + doc.confidence, 0) / document_metadata.length;
    const tooltipContent = `${sourceInfo.label}: ${documentNames} (${Math.round(avgConfidence * 100)}% confidence)`;

    return (
      <div className={`${styles.sourceContainer} ${className || ''}`}>
        <TooltipHost content={tooltipContent}>
          <div className={styles.sourceIconWrapper}>
            {CacheBadge}
            <img 
              src={sourceInfo.icon} 
              alt={sourceInfo.label}
              className={styles.sourceIcon}
            />
            <span className={styles.documentInfo}>
              {document_metadata.length === 1 
                ? document_metadata[0].filename 
                : `${document_metadata.length} documents`
              } ({Math.round(avgConfidence * 100)}%)
            </span>
          </div>
        </TooltipHost>
      </div>
    );
  }

  const cacheInfo = isFromCache ? ' (Cached)' : '';
  const confidencePercent = confidence && confidence > 0 ? Math.round(confidence * 100) : null;
  const tooltipContent = confidencePercent 
    ? `${sourceInfo.label}${cacheInfo} (${confidencePercent}% confidence)`
    : `${sourceInfo.label}${cacheInfo}`;

  return (
    <div className={`${styles.sourceContainer} ${className || ''}`}>
      <TooltipHost content={tooltipContent}>
        <div className={styles.sourceIconWrapper}>
          {CacheBadge}
          <img 
            src={sourceInfo.icon} 
            alt={sourceInfo.label}
            className={styles.sourceIcon}
          />
          <span className={styles.sourceLabel}>{sourceInfo.label}</span>
          {confidencePercent && (
            <span className={styles.confidenceLabel}>({confidencePercent}%)</span>
          )}
        </div>
      </TooltipHost>
    </div>
  );
};